/**
 * `Grain` — the app-wide grain overlay. `docs/design-direction.md` §6.1.
 *
 * One fixed, `pointer-events: none` layer at z-60 over everything, tiling a **baked**
 * SVG noise data URI at 5.5–7.5% with `mix-blend-mode: soft-light` behind a
 * `@supports` guard. The recipe is `.grain` in `styles/texture.css`; this component
 * exists to guarantee the two structural facts the recipe cannot state itself:
 *
 *  1. **It is a direct child of `<body>`.** A blended fixed overlay nested inside a
 *     scroll container creates a compositing layer that can break `position: sticky`
 *     in older webviews (§6.13), so the component portals into `<body>` no matter
 *     where a screen mounts it. Mount it once, in the app shell.
 *  2. **It is never a live filter.** The noise is a data URI, baked once; running
 *     `feTurbulence` over scrolling content is what kills this direction in
 *     Telegram's Android webview (§10.2.1, §12.20).
 *
 * Two states ride on the same layer. `flick` is the one-shot grain flick of the
 * NEEDLE DROP — `.055 → .13 → .055` on a stepped keyframe over `--t32`, fired on
 * play-from-stopped and **never on resume** (§7.2), which is why it is a prop the
 * engine's owner pulses rather than a state this component keeps. `buffering` lifts
 * the layer to `.13` for as long as audio is stalled mid-track: the record dragging,
 * which any selector reads instantly (§8.8).
 *
 * The kill switch is not here either. `html[data-grain="off"]` — "low ink" mode — is
 * set by the frame probe (median scroll frame > 22ms, or `hardwareConcurrency <= 4`)
 * and by a manual toggle in the About sheet; `texture.css` reads it and strips the
 * grain, the halftone fields and the misregistration ghost. Low ink is a design
 * state, not a degradation: hairlines, mono, inks and layout all survive (§6.1).
 */

import type { JSX } from 'react';
import { createPortal } from 'react-dom';

import styles from './primitives.module.css';
import { cx } from './Rule';

export interface GrainProps {
  /**
   * Fire the one-shot NEEDLE DROP flick. Set it for the length of the animation and
   * clear it; it is a one-shot on play-from-stopped, never on resume (§7.2).
   */
  readonly flick?: boolean;
  /** Lift the layer while audio is stalled mid-track (§8.8). */
  readonly buffering?: boolean;
}

export function Grain({ flick = false, buffering = false }: GrainProps): JSX.Element {
  return createPortal(
    <div
      className={cx('grain', buffering && styles.buffering)}
      aria-hidden="true"
      data-flick={flick ? 'true' : undefined}
    />,
    document.body,
  );
}
